import { Badge, Box, Tooltip, Typography } from '@mui/material';
import type { ColumnDef } from '../common/tableFeatures';
import { StatusChip } from '../common/StatusChip';
import { clipped, dateSortValue, dateTime, selectColumn, shortDate } from '../common/tableCells';

/** The label view's slice of a device. Values are already normalised to
 *  'N/A' by the page, so the cells never have to test for null. */
export interface LabelDevice {
  id: string;
  deviceId: string;
  name: string;
  macAddress: string;
  secureSerial: string;
  secureCode: string;
  registrationStatus: 'pending' | 'verified' | 'rejected' | 'unverified_suspicious';
  createdAt: string;
  lastLabelGeneratedAt: string | null;
  status: 'online' | 'offline' | 'unknown';
}

/** TanStack column definitions for the label-management table. */
export const LABEL_COLUMNS: ColumnDef<LabelDevice>[] = [
  selectColumn<LabelDevice>((row) => row.name, 'Select all devices'),
  {
    id: 'device',
    accessorFn: (row) => row.name,
    header: 'Device ID',
    cell: ({ row }) => (
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
          {row.original.name}
        </Typography>
        <Typography variant="caption" noWrap component="div" sx={{
          color: 'text.secondary'
        }}>
          {row.original.deviceId}
        </Typography>
      </Box>
    ),
    size: 220,
  },
  {
    id: 'macAddress',
    accessorKey: 'macAddress',
    header: 'MAC Address',
    cell: ({ row }) => clipped(row.original.macAddress, 150, { mono: true }),
    size: 160,
  },
  {
    id: 'secureSerial',
    accessorKey: 'secureSerial',
    header: 'Secure Serial',
    cell: ({ row }) => clipped(row.original.secureSerial, 130, { mono: true }),
    size: 140,
  },
  {
    id: 'secureCode',
    accessorKey: 'secureCode',
    header: 'Secure Code',
    cell: ({ row }) => clipped(row.original.secureCode, 130, { mono: true }),
    size: 140,
  },
  {
    id: 'registrationStatus',
    accessorKey: 'registrationStatus',
    header: 'Status',
    cell: ({ row }) => (
      <StatusChip status={row.original.registrationStatus} type="registration" />
    ),
    size: 130,
  },
  {
    id: 'status',
    accessorKey: 'status',
    header: 'Online',
    cell: ({ row }) => <StatusChip status={row.original.status} type="device" />,
    size: 110,
  },
  {
    id: 'createdAt',
    accessorFn: (row) => dateSortValue(row.createdAt),
    header: 'Created',
    // Date only in the cell, full timestamp on hover - as before.
    cell: ({ row }) => (
      <Tooltip title={dateTime(row.original.createdAt)}>
        <Typography variant="body2" sx={{
          color: 'text.secondary'
        }}>
          {shortDate(row.original.createdAt)}
        </Typography>
      </Tooltip>
    ),
    size: 120,
  },
  {
    id: 'lastLabelGeneratedAt',
    accessorFn: (row) => dateSortValue(row.lastLabelGeneratedAt),
    header: 'Last Label Generated',
    cell: ({ row }) => {
      const generated = row.original.lastLabelGeneratedAt;
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ color: generated ? 'text.primary' : 'text.secondary' }}>
            {dateTime(generated, 'Never')}
          </Typography>
          {generated && <Badge color="primary" variant="dot" />}
        </Box>
      );
    },
    size: 200,
  },
];
